import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AppaltiPicker } from "@/components/office/MultiWorkerImport";
import { ArrowLeft, Plus, Trash2, CheckCircle2, XCircle, Send, Info } from "lucide-react";

// Colonne testuali (paste da Excel/Sheets). Gli appalti si impostano dai menu (multi-select).
const TEXT_COLS = [
  { key: "nome_scuola", label: "Nome scuola", required: true, w: "min-w-[200px]" },
  { key: "plesso_istituto", label: "Plesso / Istituto", required: false, w: "min-w-[180px]" },
  { key: "email_segreteria", label: "Email segreteria", required: false, w: "min-w-[220px]" },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyRow = (appaltoIds) => ({
  nome_scuola: "", plesso_istituto: "", email_segreteria: "",
  appalto_ids: [...(appaltoIds || [])], status: "", error: "", _sel: false,
});
const isEmptyRow = (r) => !r.nome_scuola.trim() && !r.plesso_istituto.trim() && !r.email_segreteria.trim() && (!r.appalto_ids || r.appalto_ids.length === 0);

export function MultiSchoolImport({ onClose }) {
  const [appalti, setAppalti] = useState([]);
  const [globalAppalti, setGlobalAppalti] = useState([]);
  const [rows, setRows] = useState(() => Array.from({ length: 5 }, () => emptyRow([])));
  const [addN, setAddN] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/my-appalti").then((r) => {
      setAppalti(r.data);
      if (r.data.length === 1) { setGlobalAppalti([r.data[0].id]); }
    }).catch(() => {});
  }, []);

  const defAppalti = useMemo(() => (appalti.length === 1 ? [appalti[0].id] : []), [appalti]);

  const rowErrors = (r) => {
    if (r.status === "created") return {};
    if (isEmptyRow(r)) return {};
    const errs = {};
    if (!r.nome_scuola.trim()) errs.nome_scuola = "Obbligatorio";
    const email = r.email_segreteria.trim();
    if (email && !EMAIL_RE.test(email)) errs.email_segreteria = "Email non valida";
    if (!r.appalto_ids || r.appalto_ids.length === 0) errs.appalto = "Seleziona almeno un appalto";
    return errs;
  };
  const errorsByRow = rows.map(rowErrors);
  const entered = rows.filter((r) => !isEmptyRow(r)).length;
  const createdCount = rows.filter((r) => r.status === "created").length;
  const readyIdx = rows.map((r, i) => ({ r, i, e: errorsByRow[i] }))
    .filter(({ r, e }) => !isEmptyRow(r) && r.status !== "created" && Object.keys(e).length === 0)
    .map(({ i }) => i);
  const ready = readyIdx.length;
  const toFix = rows.filter((r, i) => !isEmptyRow(r) && r.status !== "created" && Object.keys(errorsByRow[i]).length > 0).length;

  const touch = (row) => ({ ...row, status: row.status === "created" ? row.status : "", error: "" });
  const setCell = (idx, key, value) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...touch(row), [key]: value } : row)));
    setResult(null);
  };
  const setRowAppalti = (idx, ids) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...touch(row), appalto_ids: ids } : row)));
    setResult(null);
  };

  // Selezione righe + applicazione multipla degli appalti
  const selectableIdx = rows.map((r, i) => (r.status === "created" ? -1 : i)).filter((i) => i >= 0);
  const selectedIdx = rows.map((r, i) => (r._sel && r.status !== "created" ? i : -1)).filter((i) => i >= 0);
  const allSelected = selectableIdx.length > 0 && selectableIdx.every((i) => rows[i]._sel);
  const toggleAll = () => setRows((prev) => prev.map((r) => (r.status === "created" ? r : { ...r, _sel: !allSelected })));
  const toggleRow = (idx) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, _sel: !r._sel } : r)));

  const applyAll = () => {
    setRows((prev) => prev.map((row) => (row.status === "created" ? row : { ...touch(row), appalto_ids: [...globalAppalti] })));
    setResult(null);
    toast.success("Appalti applicati a tutte le righe");
  };
  const applySelected = () => {
    setRows((prev) => prev.map((row) => (row._sel && row.status !== "created" ? { ...touch(row), appalto_ids: [...globalAppalti] } : row)));
    setResult(null);
    toast.success(`Appalti applicati a ${selectedIdx.length} ${selectedIdx.length === 1 ? "riga" : "righe"}`);
  };

  const addRows = (n) => {
    const count = Math.max(1, Math.min(500, Math.floor(Number(n) || 0)));
    setRows((prev) => [...prev, ...Array.from({ length: count }, () => emptyRow(globalAppalti.length ? globalAppalti : defAppalti))]);
    setResult(null);
  };
  const deleteRow = (idx) => {
    setRows((prev) => (prev.length <= 1 ? [emptyRow(globalAppalti.length ? globalAppalti : defAppalti)] : prev.filter((_, i) => i !== idx)));
    setResult(null);
  };

  // Copia/incolla: solo campi testuali
  const handlePaste = (e, r, c) => {
    const text = e.clipboardData?.getData("text");
    if (!text) return;
    e.preventDefault();
    let lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
    const parsed = lines.map((l) => l.split("\t"));
    setRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      const needed = r + parsed.length;
      while (next.length < needed) next.push(emptyRow(globalAppalti.length ? globalAppalti : defAppalti));
      parsed.forEach((cells, ri) => {
        cells.forEach((val, ci) => {
          const colIdx = c + ci;
          if (colIdx >= TEXT_COLS.length) return;
          if (next[r + ri].status === "created") return;
          next[r + ri][TEXT_COLS[colIdx].key] = (val ?? "").trim();
          next[r + ri].status = ""; next[r + ri].error = "";
        });
      });
      return next;
    });
    setResult(null);
  };

  const submit = async () => {
    if (ready === 0 || submitting) return;
    setSubmitting(true);
    let created = 0, error = 0;
    const updates = {};
    for (const idx of readyIdx) {
      const r = rows[idx];
      try {
        await api.post("/admin/schools", {
          nome_scuola: r.nome_scuola.trim(),
          plesso_istituto: r.plesso_istituto.trim(),
          email_segreteria: r.email_segreteria.trim(),
          appalto_ids: r.appalto_ids,
        });
        updates[idx] = { status: "created", error: "" }; created += 1;
      } catch (err) {
        const detail = formatApiError(err.response?.data?.detail) || err.message || "Errore";
        updates[idx] = { status: "error", error: detail }; error += 1;
      }
    }
    setRows((prev) => prev.map((row, i) => (updates[i] ? { ...row, ...updates[i], _sel: false } : row)));
    setResult({ created, error });
    setSubmitting(false);
    if (created > 0) toast.success(`${created} ${created === 1 ? "scuola creata" : "scuole create"}`);
    if (error > 0) toast.error(`${error} ${error === 1 ? "riga non creata" : "righe non create"}`);
  };

  const statusBadge = (r) => {
    if (r.status === "created") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="ms-row-created"><CheckCircle2 className="h-3.5 w-3.5" />Creata</span>;
    if (r.status === "error") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-destructive" data-testid="ms-row-error"><XCircle className="h-3.5 w-3.5" />Errore</span>;
    return null;
  };

  return (
    <div data-testid="multi-school-import">
      <button data-testid="ms-back-btn" onClick={onClose}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
        <ArrowLeft className="h-4 w-4" /> Torna alle Scuole
      </button>

      <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">Inserimento multiplo scuole</h2>
      <p className="text-sm text-slate-500 mb-4">Compila la tabella o incolla da Excel / Google Sheets. Ogni riga valida crea una scuola, esattamente come "Aggiungi".</p>

      <div className="mb-4 rounded-lg bg-[#EDEAF6] border border-[#D6D0EA] text-slate-700 text-sm px-3 py-2 flex items-start gap-2" data-testid="ms-paste-hint">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
        <span>Colonne attese in ordine: Nome scuola, Plesso / Istituto, Email segreteria. Gli appalti si impostano dai menu a tendina o con l'applicazione multipla.</span>
      </div>

      {/* Appalti per tutte */}
      <div className="mb-4 flex flex-wrap items-end gap-3" data-testid="ms-global-appalti-row">
        <div className="min-w-[260px]">
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Appalti per le scuole</label>
          <AppaltiPicker value={globalAppalti} onChange={setGlobalAppalti} appalti={appalti} placeholder="Seleziona uno o più appalti…" testid="ms-global-appalti" />
        </div>
        <Button variant="outline" size="sm" data-testid="ms-apply-all-btn" disabled={globalAppalti.length === 0} onClick={applyAll}
          className="rounded-lg bg-white text-slate-700 border-slate-300 hover:bg-slate-50">
          Applica a tutte le righe
        </Button>
        {selectedIdx.length > 0 && (
          <Button variant="outline" size="sm" data-testid="ms-apply-selected-btn" disabled={globalAppalti.length === 0} onClick={applySelected}
            className="rounded-lg bg-white text-slate-700 border-slate-300 hover:bg-slate-50">
            Applica a {selectedIdx.length} selezionate
          </Button>
        )}
      </div>

      {/* Riepilogo conteggi */}
      <div className="mb-3 flex flex-wrap items-center gap-4 text-sm" data-testid="ms-summary">
        <span className="font-semibold text-slate-700">{entered} inserite</span>
        <span className="text-emerald-600 font-semibold" data-testid="ms-ready-count">{ready} pronte</span>
        {toFix > 0 && <span className="text-amber-600 font-semibold" data-testid="ms-tofix-count">{toFix} da correggere</span>}
        {createdCount > 0 && <span className="text-slate-500">{createdCount} già create</span>}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-[#F1EEF9] text-slate-600">
              <th className="px-2 py-2 w-10"><Checkbox checked={allSelected} data-testid="ms-select-all" onCheckedChange={toggleAll} /></th>
              <th className="px-2 py-2 text-left font-semibold w-10">#</th>
              {TEXT_COLS.map((col) => (
                <th key={col.key} className={`px-2 py-2 text-left font-semibold ${col.w}`}>{col.label}{col.required && <span className="text-destructive"> *</span>}</th>
              ))}
              <th className="px-2 py-2 text-left font-semibold min-w-[200px]">Appalti<span className="text-destructive"> *</span></th>
              <th className="px-2 py-2 text-left font-semibold min-w-[110px]">Stato</th>
              <th className="px-2 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => {
              const errs = errorsByRow[r];
              const locked = row.status === "created";
              return (
                <tr key={r} className={`border-t border-slate-100 ${locked ? "bg-emerald-50/40" : row.status === "error" ? "bg-amber-50/30" : ""}`} data-testid={`ms-row-${r}`}>
                  <td className="px-2 py-1 text-center align-middle">
                    <Checkbox checked={!!row._sel} disabled={locked} data-testid={`ms-select-${r}`} onCheckedChange={() => toggleRow(r)} />
                  </td>
                  <td className="px-2 py-1 text-slate-400 text-xs text-center">{r + 1}</td>
                  {TEXT_COLS.map((col, c) => (
                    <td key={col.key} className="px-1 py-1 align-top">
                      <input
                        data-testid={`ms-cell-${r}-${col.key}`}
                        disabled={locked}
                        value={row[col.key]}
                        onChange={(e) => setCell(r, col.key, e.target.value)}
                        onPaste={(e) => handlePaste(e, r, c)}
                        className={`w-full h-9 px-2 rounded-md border text-sm bg-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errs[col.key] ? "border-destructive bg-destructive/5" : "border-slate-200"} ${locked ? "text-slate-400" : "text-slate-800"}`}
                        title={errs[col.key] || ""}
                      />
                      {errs[col.key] && <p className="text-[11px] text-destructive mt-0.5 px-1">{errs[col.key]}</p>}
                    </td>
                  ))}
                  <td className="px-1 py-1 align-top min-w-[200px]">
                    <AppaltiPicker value={row.appalto_ids} onChange={(ids) => setRowAppalti(r, ids)} appalti={appalti}
                      placeholder="Seleziona…" disabled={locked} testid={`ms-cell-${r}-appalto`} />
                    {errs.appalto && <p className="text-[11px] text-destructive mt-0.5 px-1">{errs.appalto}</p>}
                  </td>
                  <td className="px-2 py-1 align-middle">
                    {statusBadge(row)}
                    {row.error && row.status === "error" && <p className="text-[11px] text-slate-500 mt-0.5 max-w-[160px]">{row.error}</p>}
                  </td>
                  <td className="px-1 py-1 text-center align-middle">
                    <button data-testid={`ms-delete-row-${r}`} onClick={() => deleteRow(r)}
                      className="text-slate-400 hover:text-destructive transition-colors p-1" title="Elimina riga">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" data-testid="ms-add-row-btn" onClick={() => addRows(1)} className="rounded-lg">
          <Plus className="h-4 w-4 mr-1.5" /> Aggiungi riga
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Numero righe</span>
          <input type="number" min={1} max={500} value={addN} data-testid="ms-add-n-input"
            onChange={(e) => setAddN(e.target.value)}
            className="w-20 h-9 px-2 rounded-md border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          <Button variant="outline" size="sm" data-testid="ms-add-n-btn" onClick={() => addRows(addN)} className="rounded-lg">Aggiungi</Button>
        </div>
      </div>

      {result && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid="ms-result-banner">
          <p className="font-heading font-bold text-slate-900 mb-2">Inserimento completato</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold"><CheckCircle2 className="h-4 w-4" />{result.created} create</span>
            {result.error > 0 && <span className="inline-flex items-center gap-1.5 text-destructive font-semibold"><XCircle className="h-4 w-4" />{result.error} non create</span>}
          </div>
          {result.error > 0 && <p className="text-xs text-slate-500 mt-2">Le righe non create restano modificabili: correggile e premi di nuovo "Crea scuole". Le scuole già create non verranno ricreate.</p>}
        </div>
      )}

      <div className="mt-6 sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-4 pb-2 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-700" data-testid="ms-ready-label">{ready} {ready === 1 ? "scuola pronta" : "scuole pronte"}</p>
        <Button data-testid="ms-submit-btn" onClick={submit} disabled={ready === 0 || submitting} className="rounded-lg font-semibold">
          <Send className="h-4 w-4 mr-2" />{submitting ? "Creazione in corso…" : `Crea ${ready} ${ready === 1 ? "scuola" : "scuole"}`}
        </Button>
      </div>
    </div>
  );
}
